import { useState } from 'react'
import CheckboxInput, { CheckboxValue } from './CheckboxInput'
import { Info } from 'react-feather'
import { Tooltip } from '@mui/material'

export interface CheckboxItem {
  id: number | string
  name: string
  checked: CheckboxValue
  children?: CheckboxItem[]
  count?: number
  description?: string
}

export default function CheckboxList({
  items,
  onCheck,
  level = 0,
  expandedItems: externalExpandedItems,
  onExpandChange
}: Readonly<{
  items: ReadonlyArray<CheckboxItem>
  onCheck: (value: boolean, indices: ReadonlyArray<number>) => void
  level?: number
  expandedItems?: Set<string | number> // Optional prop for controlled expansion
  onExpandChange?: (itemId: string | number, isExpanded: boolean) => void // Optional callback
}>) {
  // Internal expanded state if not controlled externally
  const [internalExpandedItems, setInternalExpandedItems] = useState<Set<string | number>>(new Set())

  // Use external or internal expanded state
  const expandedItems = externalExpandedItems || internalExpandedItems

  const toggleAccordion = (itemId: string | number) => {
    if (onExpandChange) {
      // Controlled mode
      onExpandChange(itemId, !expandedItems.has(itemId))
    } else {
      // Uncontrolled mode
      setInternalExpandedItems(prev => {
        const next = new Set(prev)
        if (next.has(itemId)) {
          next.delete(itemId)
        } else {
          next.add(itemId)
        }
        return next
      })
    }
  }

  return (
      <ul className={`rounded-md ${level === 0 ? 'w-3/4 border-1 border-gray-light' : ''}`}>
      {items.map((item, index) => {
        const hasChildren = item.children && item.children.length > 0
        const isExpanded = expandedItems.has(item.id)

        return (
          <li key={item.id} className={`${level === 0 ? 'border-b-1 border-gray-light' : ''}`}>
            <div
              className={`
              flex items-center justify-between 
              ${level === 0 ? 'px-4 py-2' : 'px-4 py-2'} 
              hover:bg-gray-50 rounded-md
            `}
              onClick={e => {
                e.stopPropagation()
                if (isExpanded && hasChildren) {
                  return
                }
                toggleAccordion(item.id)
              }}
            >
              <div className='flex items-center flex-1'>
                <CheckboxInput
                  id={`checkbox-${item.id}`}
                  checked={item.checked}
                  label={item.name}
                  onChange={event => {
                    event.stopPropagation()
                    onCheck(event.target.checked, [index])
                  }}
                  className='mr-2'
                />

                {item.count !== undefined && (
                  <span className='ml-2 px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-sm'>{item.count}</span>
                )}

                {item.description && !hasChildren && (
                  <Tooltip title={item.description} placement='right'>
                    <Info size={16} className='cursor-pointer pl-1' />
                  </Tooltip>
                )}
              </div>

              {hasChildren && (
                <button
                  className='p-1 hover:bg-gray-100 rounded-full'
                  onClick={e => {
                    e.stopPropagation()
                    toggleAccordion(item.id)
                  }}
                >
                  <svg
                    className={`w-4 h-4 transform transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                  >
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
                  </svg>
                </button>
              )}
            </div>

            {hasChildren && (
              <div
                className={`
                  ml-6 mt-2 
                  transition-all duration-200 ease-in-out 
                  ${isExpanded ? 'block' : 'hidden'}
                `}
              >
                <CheckboxList
                  items={item.children || []}
                  onCheck={(newValue, indices) => {
                    onCheck(newValue, [index, ...indices])
                  }}
                  level={level + 1}
                  expandedItems={expandedItems}
                  onExpandChange={onExpandChange}
                />
              </div>
            )}
          </li>
        )
      })}
    </ul>
  )
}
